/**
 * Service Invoicing read model (FIN-D6) — pure: no Firestore, no React, the clock is passed in.
 *
 * WHAT IT ANSWERS for a biller: "which tickets do I invoice, under which PAYING customer, how old are they, what else
 * is open for that customer, and which tickets cannot be billed until someone picks a billing account?"
 *
 * THE DEFINITIONS (every number on the screen and on the Dashboard tile comes from here, so they cannot disagree):
 *  - READY: Status `5 Completed` / `6 Reviewed`, not archived / soft-deleted, no `billedAt`, no QuickBooks invoice stamp.
 *    (`7 Billed` is terminal, so the 18k+ billed backlog is excluded structurally, the same rule as useReadyToBillTickets.)
 *  - HELD: a READY ticket with a `billingHoldReason`. Shown, counted as held, NOT counted as "needs billing".
 *  - PAYING CUSTOMER: the ticket's resolved QuickBooks customer (`serviceTickets.qboCustomerId`), rolled up ONE level to its
 *    QuickBooks `ParentRef` inside the SAME company file (realm) — the rule autoBill's `checkInvoiceCustomer` uses.
 *    A ticket with no resolved customer goes to NEEDS BILLING ACCOUNT; it is never grouped by name text, because a guessed
 *    customer is an invoice to the wrong party.
 *  - AGE: whole days from the ticket's Scheduled Finish Date (else Start, else Created) to now. Tickets carry no
 *    completion date, so this is "days since the work was scheduled to finish". Bands 0-7 / 8-30 / 31+, plus `no-date`
 *    so an undated ticket is never mistaken for a fresh one.
 *  - INVOICED NOT BILLED: a QuickBooks invoice exists (`qboInvoiceId` / `invoiceCreatedAt`) but OfficeHub has no `billedAt`.
 *  - CREATED NOT SENT: billed through an invoice draft whose send has not been seen (`invoiceSentAt === null`).
 *
 * ONE ROW PER TICKET, AND THE SIBLING CONTRACT: two log documents with one Service ID are one ticket. If ANY log of a Service ID
 * (archived, soft-deleted, any status, in the live collection OR the archive collection) shows it billed, invoiced or at
 * 7 Billed, no other log of it is offered; it is listed
 * under `blockedBySibling` so it never vanishes silently. That only works if the caller passes `siblingLogs` (see BuildInput):
 * the ready/open status query never returns the archived `7 Billed` log of a billed ticket. Otherwise the first log is shown,
 * the rest are counted in `duplicateLogsDropped`, `logCount` says how many live logs the ticket has, and a hold on ANY live
 * log holds the ticket. A ticket with `logCount` above 1 is not safe to batch-invoice.
 *
 * WHICH STATUSES CAN BE ROWS: only what the loader reads. Ready rows are 5/6; "invoiced not billed" and "needs a look" rows
 * appear at the ready and open statuses (a ticket declined or cancelled after an invoice was stamped, without billedAt, is
 * not read: named limit). "Created, not sent" rows are read at ANY status (the loader asks for `invoiceSentAt == null`).
 *
 * KNOWN LIMITS: the parent roll-up is one level (as in autoBill); a date string in MM/DD/YYYY is read in the browser's
 * time zone, which can move an age by a day at the edge of a band.
 */
import { parseAppSheetBoolean } from './yesNo';
import { isSoftDeleted } from './softDelete';
import { toDateSafe } from './timestamp';
import { hasInvoiceCreated, isCreatedNotSent } from './invoiceLifecycle';
import { CLOSED_TICKET_STATUSES, OPEN_TICKET_STATUSES, READY_TO_BILL_STATUSES } from './ticketStatuses';

/** The one shared list (see ticketStatuses.ts); compared EXACTLY, because the server compares exactly. */
export const READY_STATUSES: readonly string[] = READY_TO_BILL_STATUSES;
export const CLOSED_STATUSES: readonly string[] = CLOSED_TICKET_STATUSES;
const OPEN_STATUSES: readonly string[] = OPEN_TICKET_STATUSES;

export const AGE_BAND_LABELS = ['0-7', '8-30', '31+', 'no-date'] as const;
export type AgeBand = (typeof AGE_BAND_LABELS)[number];

export type NeedsAccountReason = 'no-service-id' | 'no-ticket-record' | 'no-customer' | 'ambiguous';

export type CustomerLink =
  | { readonly kind: 'resolved'; readonly qboCustomerId: string; readonly realmId: string }
  | { readonly kind: 'none' }
  | { readonly kind: 'ambiguous' };

export interface QboCustomerInfo {
  readonly qboId: string;
  readonly realmId: string;
  readonly displayName: string;
  readonly parentId: string | null;
}

export interface ServiceLogInput {
  readonly id: string;
  readonly data: Record<string, unknown>;
}

export interface InvoicingRow {
  readonly id: string;
  readonly serviceId: string;
  readonly customerName: string;
  readonly status: string;
  readonly branch: string;
  readonly technician: string;
  readonly billableHours: number | null;
  readonly partsCount: number;
  readonly invoiceNumber: string;
  /** The ticket's `Service Type` (PM, repair, ...), as AppSheet spells it; blank when the log has none. */
  readonly serviceType: string;
  /** Held if ANY live log of the ticket carries a hold reason (not only the log that became this row). */
  readonly holdReason: string;
  /** How many live log documents this ticket (Service ID) has, siblings included. Anything above 1 is not safe to batch. */
  readonly logCount: number;
  readonly ageDays: number | null;
  readonly ageBand: AgeBand;
}

export interface NeedsAccountRow extends InvoicingRow {
  readonly reason: NeedsAccountReason;
}

export interface CustomerGroup {
  /** `${realmId}:${payingCustomerQboId}` — the realm is part of the key because QuickBooks ids repeat across company files. */
  readonly key: string;
  readonly name: string;
  /** False when the paying customer is not in qbo_customers: `name` is then the ticket's own text, not the payer's name. */
  readonly nameResolved: boolean;
  readonly ready: readonly InvoicingRow[];
  /** The same paying customer's open tickets at earlier stages (never ready, never finished). */
  readonly other: readonly InvoicingRow[];
}

export interface ModelCounts {
  /** Ready tickets, held or not, in a group or in Needs billing account. */
  readonly readyTotal: number;
  readonly held: number;
  /** readyTotal - held: the Dashboard tile "X tickets need to be billed". */
  readonly needsBilling: number;
  /** Bands over exactly the `needsBilling` tickets. */
  readonly needsBillingByBand: Readonly<Record<AgeBand, number>>;
  readonly needsBillingAccount: number;
  /** Of the `needsBilling` count, how many sit in Needs billing account (ready, not held, but not invoiceable until a payer is chosen). */
  readonly needsBillingAccountToBill: number;
  readonly groups: number;
  readonly groupsWithMultipleReady: number;
  /** Extra logs of a Service ID already listed (one ticket, two log documents): shown once, the rest counted here. */
  readonly duplicateLogsDropped: number;
  /** Ready-looking tickets withheld because ANOTHER log of the same Service ID is billed / invoiced. */
  readonly blockedBySibling: number;
  /** Live logs that still say 5/6 but already carry billedAt (the status mirror lags). */
  readonly billedButReadyStatus: number;
}

export interface ServiceInvoicingModel {
  readonly groups: readonly CustomerGroup[];
  readonly needsBillingAccount: readonly NeedsAccountRow[];
  readonly invoicedNotBilled: readonly InvoicingRow[];
  readonly createdNotSent: readonly InvoicingRow[];
  /** Withheld from ready because a sibling log of the same Service ID is billed. Shown so a ticket never vanishes silently. */
  readonly blockedBySibling: readonly InvoicingRow[];
  readonly billedButReadyStatus: readonly InvoicingRow[];
  readonly counts: ModelCounts;
}

export interface BuildInput {
  /** The candidate logs: every log in a ready or open status. Only these can become rows. */
  readonly logs: readonly ServiceLogInput[];
  /**
   * REQUIRED CONTRACT: every log document of every candidate Service ID, in ANY status, archived and soft-deleted included
   * (the loader fills it with a by-Service-ID query, chunks of 30). A billed ticket's other log is usually archived and at
   * `7 Billed`, which the ready/open status query never returns, so without this the double-bill guard cannot see it.
   * Entries already in `logs` may repeat here. Siblings are never rows; they only block, count and hold.
   */
  readonly siblingLogs: readonly ServiceLogInput[];
  /**
   * Logs of the candidate Service IDs from `appsheet_service_logs_archive`, a SECOND store the live collection does not
   * contain. They count ONLY as billed evidence: never rows, never in `logCount`, never a hold. Optional so older callers
   * compile; the loader always fills it, and a loader that forgets it re-opens the double-bill window the archive closes.
   */
  readonly archiveLogs?: readonly ServiceLogInput[];
  /** Keyed by the log's trimmed `Service ID`. A missing key means "no ticket record". */
  readonly links: ReadonlyMap<string, CustomerLink>;
  readonly customers: readonly QboCustomerInfo[];
  readonly now: Date;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const MAX_PLAUSIBLE_AGE_DAYS = 365 * 20;
const AGE_DATE_FIELDS = ['Scheduled Finish Date', 'Scheduled Start Date', 'Created'] as const;

/** Whole days from `value` to `now`; null when the value is blank or unreadable; never negative. */
export function ageInDays(value: unknown, now: Date): number | null {
  const date = toDateSafe(value);
  if (!date) return null;
  if (!Number.isFinite(now.getTime())) return null;
  const days = Math.max(0, Math.floor((now.getTime() - date.getTime()) / DAY_MS));
  // Anything older than 20 years is a mis-parse (new Date('1') is the year 2001), not a ticket: undated beats wrong.
  return days > MAX_PLAUSIBLE_AGE_DAYS ? null : days;
}

export function ageBand(days: number | null): AgeBand {
  if (days === null || !Number.isFinite(days)) return 'no-date';
  if (days <= 7) return '0-7';
  if (days <= 30) return '8-30';
  return '31+';
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** An id as trimmed text. Ids are strings by contract but arrive as numbers or missing from synced data; never throw on them. */
function idText(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function countRefs(value: unknown): number {
  return typeof value === 'string' ? value.split(',').filter((s) => s.trim()).length : 0;
}

function ageOf(data: Record<string, unknown>, now: Date): number | null {
  for (const field of AGE_DATE_FIELDS) {
    const days = ageInDays(data[field], now);
    if (days !== null) return days;
  }
  return null;
}

function toRow(log: ServiceLogInput, now: Date, facts: TicketFacts | undefined): InvoicingRow {
  const t = log.data;
  const ageDays = ageOf(t, now);
  const serviceId = idText(t['Service ID']);
  return {
    id: log.id,
    serviceId,
    customerName: text(t['Client Name']),
    status: text(t['Status']),
    branch: text(t['Branch']),
    technician: text(t['Technician']) || text(t['Assigned To']),
    billableHours: toNumber(t['Billable Hours']),
    partsCount: countRefs(t['Related Service Parts']),
    invoiceNumber: text(t['Invoice Number']),
    serviceType: text(t['Service Type']),
    holdReason: text(t['billingHoldReason']) || facts?.holdReason || '',
    logCount: facts?.logCount ?? 1,
    ageDays,
    ageBand: ageBand(ageDays),
  };
}

/** Oldest first; an undated row sorts after every dated one; ties by Service ID so the order is stable. */
function byAgeDesc(a: InvoicingRow, b: InvoicingRow): number {
  if (a.ageDays !== b.ageDays) {
    if (a.ageDays === null) return 1;
    if (b.ageDays === null) return -1;
    return b.ageDays - a.ageDays;
  }
  return a.serviceId.localeCompare(b.serviceId) || a.id.localeCompare(b.id);
}

const customerKey = (realmId: string, qboId: string): string => `${realmId}:${qboId}`;

interface Placement {
  readonly key: string;
  readonly name: string | null;
}

type ResolvedLink = Extract<CustomerLink, { kind: 'resolved' }>;

/**
 * A resolved link is only usable with BOTH a customer id and a company-file id. A blank one would key every such ticket as
 * `realm:` or `:id` and pile unrelated customers into one group, so it is treated as "no customer" instead.
 */
function usableLink(link: CustomerLink | undefined): ResolvedLink | null {
  if (link?.kind !== 'resolved') return null;
  const qboCustomerId = idText(link.qboCustomerId);
  const realmId = idText(link.realmId);
  return qboCustomerId && realmId ? { kind: 'resolved', qboCustomerId, realmId } : null;
}

/** The paying customer for a resolved link: the QuickBooks parent inside the same company file, else the customer itself. */
function placeCustomer(link: ResolvedLink, index: ReadonlyMap<string, QboCustomerInfo>): Placement {
  const own = index.get(customerKey(link.realmId, link.qboCustomerId));
  const rootId = idText(own?.parentId) || link.qboCustomerId;
  const root = rootId === link.qboCustomerId ? own : index.get(customerKey(link.realmId, rootId));
  return { key: customerKey(link.realmId, rootId), name: root?.displayName?.trim() || null };
}

type Disposition =
  | { readonly kind: 'skip' }
  | { readonly kind: 'invoicedNotBilled' }
  | { readonly kind: 'createdNotSent' }
  | { readonly kind: 'billedButReadyStatus' }
  | { readonly kind: 'ready' }
  | { readonly kind: 'open' };

/** A ticket whose Status says billed, however it is spelled (err toward "billed": the dangerous error is offering it again). */
const saysBilled = (status: unknown): boolean => typeof status === 'string' && status.trim().toLowerCase() === '7 billed';

/** What a live (not archived, not deleted) ticket is to the biller. */
function classify(data: Record<string, unknown>): Disposition {
  // EXACT comparison: the server refuses ' 6 Reviewed' or '6 reviewed', so the screen must not offer them.
  const status = typeof data['Status'] === 'string' ? data['Status'] : '';
  const billed = Boolean(data['billedAt']);
  // Created through a draft and not yet SENT stays on the Mark-sent list at ANY status: the mirror moves to 7 Billed, and
  // the ticket may later be declined or cancelled, but the invoice still has to go out.
  if (billed && isCreatedNotSent(data)) return { kind: 'createdNotSent' };
  if (saysBilled(status)) return { kind: 'skip' };
  const known = READY_STATUSES.includes(status) || OPEN_STATUSES.includes(status) || CLOSED_STATUSES.includes(status);
  if (!known) return { kind: 'skip' };
  // A QuickBooks invoice exists and OfficeHub has not recorded billing: visible wherever it sits (it also blocks siblings).
  if (!billed && hasInvoiceCreated(data)) return { kind: 'invoicedNotBilled' };
  if (READY_STATUSES.includes(status)) return billed ? { kind: 'billedButReadyStatus' } : { kind: 'ready' };
  return OPEN_STATUSES.includes(status) ? { kind: 'open' } : { kind: 'skip' };
}

interface TicketFacts {
  readonly logCount: number;
  readonly holdReason: string;
}

const isLive = (data: Record<string, unknown>): boolean => !isSoftDeleted(data) && !parseAppSheetBoolean(data['Archive']);

/** Every distinct log of the candidates and their siblings, candidates first so they win a repeat. */
function allLogsOf(input: BuildInput): ServiceLogInput[] {
  const seen = new Set<string>();
  const out: ServiceLogInput[] = [];
  for (const log of [...input.logs, ...input.siblingLogs]) {
    if (seen.has(log.id)) continue;
    seen.add(log.id);
    out.push(log);
  }
  return out.sort((a, b) => a.id.localeCompare(b.id));
}

/**
 * Per Service ID, from ALL logs: whether any log (archived, deleted, any status) shows it billed or invoiced — that blocks —
 * plus the live log count and the first live hold reason, so a hold on a sibling log still holds the ticket.
 */
function ticketFacts(all: readonly ServiceLogInput[], archive: readonly ServiceLogInput[] = []): { billed: ReadonlySet<string>; facts: ReadonlyMap<string, TicketFacts> } {
  const billed = new Set<string>();
  const facts = new Map<string, TicketFacts>();
  for (const { data } of archive) {
    const serviceId = idText(data['Service ID']);
    if (serviceId && (data['billedAt'] || hasInvoiceCreated(data) || saysBilled(data['Status']))) billed.add(serviceId);
  }
  for (const { data } of all) {
    const serviceId = idText(data['Service ID']);
    if (!serviceId) continue;
    // No liveness filter here: 93% of logs are archived, and a billed ticket's other log is usually one of them.
    if (data['billedAt'] || hasInvoiceCreated(data) || saysBilled(data['Status'])) billed.add(serviceId);
    if (!isLive(data)) continue;
    const prev = facts.get(serviceId);
    facts.set(serviceId, { logCount: (prev?.logCount ?? 0) + 1, holdReason: prev?.holdReason || text(data['billingHoldReason']) });
  }
  return { billed, facts };
}

function emptyBands(): Record<AgeBand, number> {
  return { '0-7': 0, '8-30': 0, '31+': 0, 'no-date': 0 };
}

export function buildServiceInvoicingModel(input: BuildInput): ServiceInvoicingModel {
  const { links, customers, now } = input;
  // Stable order whatever the input order, so "the first log of a Service ID" is always the same one.
  const logs = [...input.logs].sort((a, b) => a.id.localeCompare(b.id));
  const index = new Map<string, QboCustomerInfo>();
  for (const c of customers) {
    const realm = idText(c.realmId);
    const id = idText(c.qboId);
    if (realm && id) index.set(customerKey(realm, id), c);
  }
  const { billed: alreadyBilled, facts } = ticketFacts(allLogsOf(input), input.archiveLogs);

  const readyByKey = new Map<string, { name: string | null; rows: InvoicingRow[] }>();
  const openByKey = new Map<string, InvoicingRow[]>();
  const needsAccount: NeedsAccountRow[] = [];
  const invoicedNotBilled: InvoicingRow[] = [];
  const createdNotSent: InvoicingRow[] = [];
  const blockedBySibling: InvoicingRow[] = [];
  const billedButReadyStatus: InvoicingRow[] = [];
  const listedServiceIds = new Set<string>();
  const openCandidates: Array<{ row: InvoicingRow; link: ResolvedLink }> = [];
  let duplicateLogsDropped = 0;

  for (const log of logs) {
    if (!isLive(log.data)) continue;
    const disposition = classify(log.data);
    if (disposition.kind === 'skip') continue;
    const row = toRow(log, now, facts.get(idText(log.data['Service ID'])));

    if (disposition.kind === 'invoicedNotBilled') { invoicedNotBilled.push(row); continue; }
    if (disposition.kind === 'createdNotSent') { createdNotSent.push(row); continue; }
    if (disposition.kind === 'billedButReadyStatus') { billedButReadyStatus.push(row); continue; }

    const link = row.serviceId ? links.get(row.serviceId) : undefined;
    const usable = usableLink(link);
    if (disposition.kind === 'open') {
      // Earlier-stage work is context for a customer who has something to bill; with no usable link it cannot be placed.
      // Placed after the loop, once every ready Service ID is known, so a ticket is never listed as both.
      if (usable) openCandidates.push({ row, link: usable });
      continue;
    }

    // READY candidate. Another log of this ticket is billed: never offer it. Another log already listed: count, don't repeat.
    if (row.serviceId && alreadyBilled.has(row.serviceId)) { blockedBySibling.push(row); continue; }
    if (row.serviceId) {
      if (listedServiceIds.has(row.serviceId)) { duplicateLogsDropped += 1; continue; }
      listedServiceIds.add(row.serviceId);
    }

    if (!row.serviceId) { needsAccount.push({ ...row, reason: 'no-service-id' }); continue; }
    if (!link) { needsAccount.push({ ...row, reason: 'no-ticket-record' }); continue; }
    if (link.kind === 'ambiguous') { needsAccount.push({ ...row, reason: 'ambiguous' }); continue; }
    if (!usable) { needsAccount.push({ ...row, reason: 'no-customer' }); continue; }

    const { key, name } = placeCustomer(usable, index);
    const bucket = readyByKey.get(key) ?? { name, rows: [] };
    bucket.rows.push(row);
    readyByKey.set(key, bucket);
  }

  const openSeen = new Set<string>();
  for (const { row, link } of openCandidates) {
    if (listedServiceIds.has(row.serviceId) || alreadyBilled.has(row.serviceId) || openSeen.has(row.serviceId)) continue;
    openSeen.add(row.serviceId);
    const { key } = placeCustomer(link, index);
    const rows = openByKey.get(key) ?? [];
    rows.push(row);
    openByKey.set(key, rows);
  }

  const groups: CustomerGroup[] = Array.from(readyByKey.entries()).map(([key, bucket]) => {
    const ready = [...bucket.rows].sort(byAgeDesc);
    const other = [...(openByKey.get(key) ?? [])].sort((a, b) => a.status.localeCompare(b.status) || a.serviceId.localeCompare(b.serviceId));
    return { key, name: bucket.name ?? ready[0].customerName, nameResolved: bucket.name !== null, ready, other };
  });
  // byAgeDesc ends on Service ID then log id, so this order is total: no further tie-break is reachable.
  groups.sort((a, b) => byAgeDesc(a.ready[0], b.ready[0]));
  needsAccount.sort(byAgeDesc);

  const allReady: readonly InvoicingRow[] = [...groups.flatMap((g) => g.ready), ...needsAccount];
  const billable = allReady.filter((r) => !r.holdReason);
  const bands = emptyBands();
  for (const r of billable) bands[r.ageBand] += 1;

  return {
    groups,
    needsBillingAccount: needsAccount,
    invoicedNotBilled: [...invoicedNotBilled].sort(byAgeDesc),
    createdNotSent: [...createdNotSent].sort(byAgeDesc),
    blockedBySibling: [...blockedBySibling].sort(byAgeDesc),
    billedButReadyStatus: [...billedButReadyStatus].sort(byAgeDesc),
    counts: {
      readyTotal: allReady.length,
      held: allReady.length - billable.length,
      needsBilling: billable.length,
      needsBillingByBand: bands,
      needsBillingAccount: needsAccount.length,
      needsBillingAccountToBill: needsAccount.filter((r) => !r.holdReason).length,
      groups: groups.length,
      groupsWithMultipleReady: groups.filter((g) => g.ready.length > 1).length,
      duplicateLogsDropped,
      blockedBySibling: blockedBySibling.length,
      billedButReadyStatus: billedButReadyStatus.length,
    },
  };
}
